import type { FilteredValues, OptionList } from '$saga/types/search.types';

/**
 * Flattens a hierarchical option tree into a single depth-first list
 * (parent, then all its descendants, before moving to the next sibling).
 */
export const flattenOptionTree = (options: OptionList): OptionList =>
    options.flatMap((option) => [
        option,
        ...flattenOptionTree(option.children ?? []),
    ]);

/**
 * Takes a search query string and find all the options that match the query
 * return the list, if query string is empty then returns whole defaultOptions list
 * @param query
 * @param defaultList
 * @param displayedList
 */
export const filterList = ({
    query,
    fullList,
    displayedList,
}: {
    query: string;
    fullList: OptionList;
    displayedList?: OptionList;
}) => {
    if (query === '') {
        return displayedList ?? fullList;
    }

    const normalizedSearchValue = query
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const results: FilteredValues = [];

    for (const option of fullList) {
        if (option.hidden) continue;

        const normalizedLabel = option.label
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

        let score = 0;

        // Calculate match score
        if (normalizedLabel === normalizedSearchValue) {
            score = 100; // Exact match
        } else if (normalizedLabel.startsWith(normalizedSearchValue)) {
            score = 75; // Starts with
        } else if (normalizedLabel.includes(normalizedSearchValue)) {
            score = 50; // Contains
        } else {
            // Fuzzy match - check for sequential characters
            let searchIndex = 0;
            let lastMatchPos = -1;

            for (let i = 0; i < normalizedSearchValue.length; i++) {
                const char = normalizedSearchValue[i];
                const pos = normalizedLabel.indexOf(char, lastMatchPos + 1);

                if (pos !== -1) {
                    // Match found
                    searchIndex++;
                    lastMatchPos = pos;
                }
            }

            // If we matched at least 70% of characters
            if (searchIndex >= normalizedSearchValue.length * 0.83) {
                score = 25 + (searchIndex / normalizedSearchValue.length) * 25;
            }
        }

        if (score > 0) {
            results.push({
                ...option,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                _score: score,
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return results.sort((a, b) => (b._score || 0) - (a._score || 0));
};
